import { useRef, useState } from 'react'
import type { TenantBootstrapModel } from '@unified-lk/tenant-config'
import { staffApi } from '../../../../api/staff'
import type { AuthSession } from '../../../../types/plans'
import type { ConstructorPlan } from '../constructorTypes'

export function MediaPanel({
  plan,
  bootstrap,
  session,
  onReload,
}: {
  plan: ConstructorPlan
  bootstrap: TenantBootstrapModel
  session: AuthSession
  onReload: () => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [togglingShare, setTogglingShare] = useState<number | null>(null)

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    try {
      await staffApi.uploadMedia(bootstrap, session, plan.id, file)
      onReload()
    } catch {
      // ignore
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  async function handleDeleteMedia(mediaId: number) {
    try {
      await staffApi.deleteMedia(bootstrap, session, plan.id, mediaId)
      onReload()
    } catch {
      // ignore
    }
  }

  async function handleToggleShare(mediaId: number, currentShared: boolean) {
    setTogglingShare(mediaId)
    try {
      await staffApi.setMediaShare(bootstrap, session, plan.id, mediaId, !currentShared)
      onReload()
    } catch {
      // ignore
    } finally {
      setTogglingShare(null)
    }
  }

  const materials = plan.materials ?? (plan.media ?? []).map(m => ({ id: m.id, title: m.file_name, type: m.file_type, share_in_patient_lk: false }))

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Файлы</h3>
        <button
          type="button"
          className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-200 disabled:opacity-50"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Загрузка...' : '+ Загрузить'}
        </button>
        <input ref={fileInputRef} type="file" className="hidden" onChange={e => void handleUpload(e)} />
      </div>

      {materials.length === 0 ? (
        <p className="py-2 text-center text-xs text-slate-400">Нет прикреплённых файлов</p>
      ) : (
        <div className="space-y-1.5">
          {materials.map(m => (
            <div key={m.id} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1.5">
              <svg className="h-4 w-4 flex-shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
              </svg>
              <span className="flex-1 truncate text-[11px] text-slate-700">{'title' in m ? m.title : (m as { file_name?: string }).file_name}</span>
              <button
                type="button"
                title={m.share_in_patient_lk ? 'Скрыть от пациента' : 'Показать пациенту'}
                className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium transition-colors disabled:opacity-50 ${m.share_in_patient_lk ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                onClick={() => void handleToggleShare(m.id, m.share_in_patient_lk ?? false)}
                disabled={togglingShare === m.id}
              >
                {m.share_in_patient_lk ? (
                  <>
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                    Пациент видит
                  </>
                ) : (
                  <>
                    <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                    </svg>
                    Скрыто
                  </>
                )}
              </button>
              <button type="button" className="text-slate-300 hover:text-red-500" onClick={() => void handleDeleteMedia(m.id)}>
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
